import { createStyles, makeStyles } from "@material-ui/core";
import {
    BenefitsView,
    CircleIcon,
    ContactView,
    DesktopPage,
    DesktopPageActions,
    DesktopPageSection,
    DesktopSideBar,
    DesktopSideBarPane,
    DesktopTabbedContent,
    JobStatusLabel,
    RelodeTheme,
    RewardsView,
    Text,
} from "@relode/core";
import * as React from "react";

import { useJobContext } from "../../../_infra";
import { JobActionOptionsDesktop } from "../jobActions/jobActionOptionsDesktop";
import { JobActionsDesktop } from "../jobActions/jobActionsDesktop";
import { JobDetailsProps } from "./jobDetailsProps";
import { JobInsights } from "./jobInsights";
import { JobOrder } from "./jobOrder";
import { JobOverview } from "./jobOverview";
import { TeamMembersDesktop } from "./teamMembersDesktop";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            circle: {
                fontSize: "0.5em",
                color: theme.palette.action.active,
                margin: theme.spacing(0, 1, 0, 1),
            },
            subtitle: {
                display: "flex",
                alignItems: "center",
            },
        }),
    { name: "JobDetailsDesktop" }
);

export const JobDetailsDesktop = (props: JobDetailsProps) => {
    const classes = useStyles(props);
    const {
        state: {
            data: { job, currentJobId },
            ui: { isJobLoading },
        },
    } = useJobContext();

    const {
        handleMessageComposer,
        handleShareJob,
        handleOpenTeamMembersModal,
        handleDeleteTeamMember,
        handleEditTeamMember,
        nextPossibleStatuses,
        handleChangeJobStatus,
        backUrl,
        handleCopyJob,
        handleEditJob,
    } = props;

    return (
        <>
            <DesktopPage
                pageTitle={job.details.jobName}
                data-cy="jobDetails-page"
                //id="jobDetails"
                isBusy={isJobLoading}
                actions={
                    <DesktopPageActions
                        backUrl={backUrl ? backUrl : "/jobs"}
                        title={job.details ? job.details.jobName : "n/a"}
                        statusLabel={
                            job.details && job.details.jobStatus ? (
                                <JobStatusLabel
                                    title={job.details.jobStatus.description}
                                    jobStatus={job.details.jobStatus}
                                />
                            ) : null
                        }
                        subtitle={
                            job.details ? (
                                <div className={classes.subtitle}>
                                    <Text
                                        variant="body1"
                                        dataCy="jobDetails-locationName-text"
                                    >
                                        {job.details.location?.locationName}
                                    </Text>
                                    <CircleIcon className={classes.circle} />
                                    <Text dataCy="jobDetails-teamName-text">
                                        {job.details.teamName}
                                    </Text>
                                </div>
                            ) : null
                        }
                        pageActionOptions={
                            <JobActionOptionsDesktop
                                shareAction={
                                    job.details.canShare ? handleShareJob : null
                                }
                                messageAction={handleMessageComposer}
                                jobCopyAction={handleCopyJob}
                                jobEditAction={handleEditJob}
                            />
                        }
                        pageActions={
                            <JobActionsDesktop
                                jobStatusAction={handleChangeJobStatus}
                                availableJobStatuses={nextPossibleStatuses}
                            />
                        }
                    ></DesktopPageActions>
                }
            >
                <DesktopTabbedContent defaultSection="details">
                    <DesktopPageSection label="details" id="details">
                        <JobOverview job={job} />
                    </DesktopPageSection>

                    <DesktopPageSection label="team" id="team">
                        <TeamMembersDesktop
                            team={job.team}
                            handleOpenTeamMembersModal={
                                handleOpenTeamMembersModal
                            }
                            handleDeleteTeamMember={handleDeleteTeamMember}
                            handleEditTeamMember={handleEditTeamMember}
                            jobStatus={job.details.jobStatus}
                        />
                    </DesktopPageSection>
                    {job.details.canViewSalary ? (
                        <DesktopPageSection label="order" id="order">
                            <JobOrder
                                order={job.order}
                                overview={job.details}
                            />
                        </DesktopPageSection>
                    ) : null}

                    <DesktopPageSection
                        label="insights"
                        id="insights"
                        count={job.details.totalAvailableCandidateCount}
                    >
                        <JobInsights />
                    </DesktopPageSection>
                </DesktopTabbedContent>

                <DesktopSideBar>
                    <DesktopSideBarPane>
                        <RewardsView
                            altTitle={"position"}
                            companyName={job.team.companyName}
                            openPositions={job.details.openPositionCount}
                            jobLocationName={job.details.location.locationName}
                            jobSalaryInUSDollars={job.details.salaryDescription}
                            jobTypeName={job.details.jobType.jobTypeName}
                            hoursPerWeek={job.details.hoursPerWeek}
                            contractStartDate={job.details.contractStartDate}
                            contractEndDate={job.details.contractEndDate}
                            jobPriorityTypeId={job.details.jobPriorityType?.id}
                            actionableLinks={false}
                        />
                    </DesktopSideBarPane>

                    {job.details.benefits.length > 0 ? (
                        <DesktopSideBarPane>
                            <BenefitsView benefits={job.details.benefits} />
                        </DesktopSideBarPane>
                    ) : null}

                    <DesktopSideBarPane>
                        <ContactView
                            label={"job manager"}
                            jobId={job.details.jobId}
                            jobName={job.details.jobName}
                            contactName={job.details.jobManager.fullName}
                            contactUserId={job.details.jobManager.userId}
                            contactInitials={job.details.jobManager.initials}
                            contactPhoneNumber={
                                job.details.jobManager.phoneNumber
                            }
                            contactProfileImageUrl={
                                job.details.jobManager.profileImageUrl
                            }
                            actionableLinks={true}
                            isClient={true}
                        />
                    </DesktopSideBarPane>
                </DesktopSideBar>
            </DesktopPage>
        </>
    );
};
